import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/UnifiedAuthContext';
import Icons from '../components/Icons';
import { API_BASE_URL } from '../utils/apiConfig';

const EventSignup = ({ event, onSignupChange }) => {
  const { user, signUpForEvent, withdrawFromEvent } = useAuth();
  const [isSignedUp, setIsSignedUp] = useState(false);
  const [signupCount, setSignupCount] = useState(event?.signups || 0);
  const [loading, setLoading] = useState(false);
  const [showPlayerList, setShowPlayerList] = useState(false);
  const [signedUpPlayers, setSignedUpPlayers] = useState([]);


  const checkSignupStatus = useCallback(async () => {
    if (!user || !event || !user.player_id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/auth/events/${event.id}/signups`, {
        credentials: 'include'
      });
      const signups = await response.json();
      
      // Check if current user is signed up (compare by player_id)
      // Handle both snake_case (player_id) and camelCase (playerId) from different endpoints
      const userPlayerId = user?.player_id || user?.playerId || user?.player?.id;
      const userSignup = signups.find(signup => {
        // Handle both string and number comparisons to fix prod vs dev issues
        return String(signup.player_id) === String(userPlayerId);
      });
      setIsSignedUp(!!userSignup);
      
      setSignupCount(signups.length);
      setSignedUpPlayers(signups);
    } catch (error) {
      console.error('Error checking signup status:', error);
    }
  }, [user, event]);

  useEffect(() => {
    checkSignupStatus();
  }, [checkSignupStatus]);

  const handleSignup = async () => {
    if (!user || !user.player_id) return;
    
    setLoading(true);
    let result;
    
    if (isSignedUp) {
      result = await withdrawFromEvent(event.id);
    } else {
      result = await signUpForEvent(event.id);
    }
    
    if (result.success) {
      // Update state immediately based on the action (optimistic update)
      const newState = !isSignedUp;
      setIsSignedUp(newState);
      
      // Then verify with server after a delay to ensure DB commit
      setTimeout(async () => {
        await checkSignupStatus();
      }, 1000);
      onSignupChange && onSignupChange();
    } else {
      alert(result.error);
      // If we get "already signed up" error, force refresh to clear caches
      if (result.error.includes('already signed up')) {
        window.location.reload();
      }
    }
    
    setLoading(false);
  };

  if (!event) return null;

  return (
    <div className="bg-gray-50 dark:bg-navy-700 p-4 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold dark:text-gray-100">Event Signup</h3>
        <button
          onClick={() => setShowPlayerList(!showPlayerList)}
          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
        >
          {showPlayerList ? 'Hide' : 'Show'} Players ({signupCount})
        </button>
      </div>

      {user ? (
        user.player_id ? (
          <button
            onClick={handleSignup}
            disabled={loading}
            className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
              isSignedUp
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-green-600 hover:bg-green-700 text-white'
            } disabled:opacity-50`}
          >
            {loading ? 'Processing...' : isSignedUp ? 'Withdraw' : 'Sign Up'}
          </button>
        ) : (
 <div className="text-center py-12">     
          <div className="flex justify-center mb-4 text-amber-600">
            <Icons.AlertTriangle className="w-12 h-12" />
          </div>
          <h3 className="text-xl font-semibold text-amber-700 mb-2">Account Linking Required</h3>
          <p className="text-amber-600 mb-6 max-w-md mx-auto">
            To sign-up for events, you need to link your account to a player profile. 
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-amber-800 text-sm font-medium mb-2">How to link your account:</p>
            <ol className="text-amber-700 text-sm text-left space-y-1">
              <li>1. Hover over you profile name in the top right</li>
              <li>2. Select Player Profile</li>
              <li>3. Select your Player name</li>
              <li>4. Click Link Account button</li>
            </ol>
          </div>
          </div>
        )
      ) : (
        <div className="text-center py-4 text-gray-600 dark:text-gray-400">
          Please log in to sign up for events
        </div>
      )}

      {showPlayerList && (
        <div className="mt-4 border-t dark:border-gray-600 pt-4">
          <h4 className="font-medium mb-2 dark:text-gray-200">Signed Up Players:</h4>
          {signedUpPlayers.length > 0 ? (
            <ul className="space-y-1">
              {signedUpPlayers.map((signup, index) => (
                <li key={index} className="text-sm text-gray-700 dark:text-gray-300">
                  {signup.player_name || `${signup.first_name} ${signup.last_name}`.trim() || signup.username}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">No players signed up yet</p>
          )}
        </div>
      )}
    </div>
  );
};

export default EventSignup;